import { isSameDay, isSameWeek, subDays } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

export const TIME_ZONE = 'America/Denver';

export const formatOrderDate = (dateString) => {
  if (!dateString) return '';

  const date = toZonedTime(dateString, TIME_ZONE);
  const now = toZonedTime(new Date(), TIME_ZONE);

  if (isSameDay(date, now)) {
    return `Today at ${formatInTimeZone(dateString, TIME_ZONE, 'p')}`;
  }
  if (isSameDay(date, subDays(now, 1))) {
    return `Yesterday at ${formatInTimeZone(dateString, TIME_ZONE, 'p')}`;
  }
  if (isSameWeek(date, now, { weekStartsOn: 1 })) {
    return `${formatInTimeZone(dateString, TIME_ZONE, 'EEEE')} at ${formatInTimeZone(dateString, TIME_ZONE, 'p')}`;
  }
  return formatInTimeZone(dateString, TIME_ZONE, "MMM d, yyyy 'at' p");
};

export const formatShortDate = (dateString) => {
  if (!dateString) return '';
  return formatInTimeZone(dateString, TIME_ZONE, 'MMM d, h:mm a');
};

export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') return null;
  const number = Number(amount);
  if (isNaN(number)) return amount;
  return number.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// Subtotal with refunded line items excluded when the order mixes
// refunded and non-refunded items
export const computeOrderSubtotal = (order) => {
  const items = order.OrderLineItems || [];
  const hasRef = items.some((li) => li.refunded);
  const hasNonRef = items.some((li) => !li.refunded);
  return items.reduce((acc, item) => {
    if (hasRef && hasNonRef && item.refunded) return acc;
    return acc + +item.subtotal;
  }, 0);
};

export const computeMargin = (order) => {
  const gross = parseFloat(order.gross_sales);
  const landed = parseFloat(order.landed_cost);
  if (!gross || isNaN(gross) || isNaN(landed)) return null;
  return ((gross - landed) / gross) * 100;
};

export const getShopifyOrderUrl = (externalOrderId) =>
  `https://admin.shopify.com/store/for-a-kings-loot/orders/${externalOrderId}`;

export const getAmazonOrderUrl = (externalOrderId) =>
  `https://sellercentral.amazon.com/orders-v3/order/${externalOrderId}`;

export const marginToneClass = (margin) => {
  if (margin >= 60) return 'text-green-700';
  if (margin >= 40) return 'text-gray-900';
  return 'text-amber-600';
};

export const STATUS_DOT_COLORS = (status, channel) => {
  if (!status) return 'bg-gray-400 text-gray-600';
  const s = status.toLowerCase();
  if (channel === 'Shopify') {
    switch (s) {
      case 'paid':
        return 'bg-green-600 text-green-700';
      case 'pending':
      case 'authorized':
      case 'partially_paid':
        return 'bg-amber-500 text-amber-600';
      case 'refunded':
      case 'partially_refunded':
        return 'bg-red-600 text-red-600';
      case 'expired':
      case 'voided':
        return 'bg-gray-400 text-gray-500';
      default:
        return 'bg-gray-400 text-gray-500';
    }
  }
  if (channel === 'Amazon') {
    switch (s) {
      case 'pending':
      case 'pendingavailability':
      case 'unshipped':
      case 'partiallyshipped':
      case 'invoiceunconfirmed':
        return 'bg-amber-500 text-amber-600';
      case 'shipped':
        return 'bg-gray-900 text-gray-900';
      case 'canceled':
      case 'unfulfillable':
        return 'bg-red-600 text-red-600';
      default:
        return 'bg-gray-400 text-gray-500';
    }
  }
  return 'bg-gray-400 text-gray-500';
};
